import {
  Card,
  Icon,
  type IconProps,
  Inline,
  Separator,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@omnipresentgroup/design-system';
import { TimesheetArbeitszeitTimeSlotTypeEnum } from '@omnipresentgroup/types';
import { Duration } from 'luxon';
import { memo } from 'react';

import { MAX_TOTAL_HOURS_PER_WEEK, TIMESLOT_TYPES_CONFIG } from '../constants';

const DEFAULT_VALUE = 'PT0H';

export const TimesheetSummaryCard = memo(
  ({
    summary,
    isLoading,
  }: {
    isLoading?: boolean;
    summary?: Partial<Record<TimesheetArbeitszeitTimeSlotTypeEnum, string>>;
  }) => {
    const SummaryItem = ({
      id,
      label,
      value,
      icon,
    }: {
      id: string;
      label: string;
      value?: string;
      icon?: IconProps['icon'];
    }) => {
      const valueinHours = Duration.fromISO(value || DEFAULT_VALUE).shiftTo(
        'hours',
      );

      return (
        <Inline w="stretch" align="center" css={{ whiteSpace: 'nowrap' }}>
          {icon && <Icon icon={icon} size="24" />}
          <Stack gap="8" w="stretch">
            <Typography
              as="label"
              size="12"
              weight="medium"
              color="subtle"
              id={id}
            >
              {label}
            </Typography>
            <Inline gap="4" align="center">
              {valueinHours.hours > MAX_TOTAL_HOURS_PER_WEEK && (
                <Tooltip
                  description="Note: Clients need to justify to local authorities any work that exceeds standard weekly or contracted limits."
                  id="max-total-hours-warning"
                >
                  <Icon icon="AlertTriangle" size="16" color="negative" />
                </Tooltip>
              )}

              <Typography
                as="span"
                size="16"
                weight="medium"
                color={
                  valueinHours.hours > MAX_TOTAL_HOURS_PER_WEEK
                    ? 'negative'
                    : 'default'
                }
                aria-labelledby={id}
              >
                {isLoading ? (
                  <Skeleton width={60} />
                ) : (
                  valueinHours.toHuman({
                    unitDisplay: 'short',
                  })
                )}
              </Typography>
            </Inline>
          </Stack>
        </Inline>
      );
    };

    return (
      <Card p="24" overflow="auto">
        <Inline gap="24">
          <SummaryItem
            id="summary-item-work"
            label="Work"
            icon={
              TIMESLOT_TYPES_CONFIG.get(
                TimesheetArbeitszeitTimeSlotTypeEnum.WORK,
              )?.icon
            }
            value={summary?.work}
          />
          <Separator />
          <SummaryItem
            id="summary-item-overtime"
            label="Overtime"
            icon={
              TIMESLOT_TYPES_CONFIG.get(
                TimesheetArbeitszeitTimeSlotTypeEnum.OVERTIME,
              )?.icon
            }
            value={summary?.overtime}
          />
          <Separator />
          <SummaryItem
            id="summary-item-break"
            label="Break"
            icon={
              TIMESLOT_TYPES_CONFIG.get(
                TimesheetArbeitszeitTimeSlotTypeEnum.BREAK,
              )?.icon
            }
            value={summary?.break}
          />
        </Inline>
      </Card>
    );
  },
);
